/**
 * BehaviorTab - Spending Breakdown, Bounce Events Log, Repayment Heatmap
 */

import React from 'react';
import type {
  BehaviorTabProps,
  BounceTransaction,
} from '../../../../types/lending-report.types';
import { formatINR } from '../../../../types/lending-report.types';
import { formatDate } from '../../../../utils/lending-report-utils';

const getTypeBadge = (type: string): { bg: string; color: string } => {
  const upper = type.toUpperCase();
  if (upper.includes('NACH')) return { bg: '#dcfce7', color: '#15803d' };
  if (upper.includes('SI')) return { bg: '#dbeafe', color: '#1d4ed8' };
  return { bg: '#f3f4f6', color: '#374151' };
};

const getSeverity = (amount: number): { label: string; color: string } => {
  if (amount >= 10000) return { label: 'Critical', color: '#dc2626' };
  if (amount >= 5000) return { label: 'High', color: '#dc2626' };
  return { label: 'Medium', color: '#d97706' };
};

const HEATMAP_COLORS: Record<string, string> = {
  'on-time': '#4ade80',
  'late': '#fbbf24',
  'bounced': '#f87171',
  'no-data': '#e5e7eb',
};

/* ─── Component ─── */

export const BehaviorTab: React.FC<BehaviorTabProps> = ({ report }) => {
  // Flatten all bounce transactions across months
  const allTransactions: (BounceTransaction & { severity: { label: string; color: string } })[] =
    (report.bounceLog ?? []).flatMap((entry) =>
      entry.transactions.map((txn) => ({
        ...txn,
        severity: getSeverity(txn.amount),
      }))
    );

  return (
    <div className="space-y-6">
      {/* ─── 1. Spending Breakdown ─── */}
      <div>
        <h3 style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '12px' }}>
          Spending Breakdown
        </h3>
        {(!report.spendingBreakdown || report.spendingBreakdown.length === 0) ? (
          <div style={{ padding: '24px', textAlign: 'center', color: '#9ca3af', fontSize: '13px', backgroundColor: '#f9fafb', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
            No spending data available
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
            {report.spendingBreakdown.map((item) => (
              <div
                key={item.group}
                style={{
                  padding: '16px',
                  borderRadius: '12px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                }}
              >
                <div style={{ fontSize: '12px', fontWeight: 500, color: '#6b7280', marginBottom: '8px' }}>
                  {item.group}
                </div>
                <div style={{ fontSize: '24px', fontWeight: 700, color: '#111827' }}>
                  {formatINR(item.totalAmount)}
                </div>
                <div style={{ fontSize: '12px', fontWeight: 500, color: '#16a34a', marginTop: '4px' }}>
                  {item.percentOfIncome.toFixed(1)}% of income
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ─── 2. Bounce Events Log ─── */}
      <div>
        <h3 style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '12px' }}>
          Bounce Events Log
        </h3>
        {allTransactions.length === 0 ? (
          <div style={{ padding: '24px', textAlign: 'center', color: '#9ca3af', fontSize: '13px', backgroundColor: '#f9fafb', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
            No bounce events recorded
          </div>
        ) : (
          <div style={{ borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Date</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Type</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Amount</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Reason</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Severity</th>
                </tr>
              </thead>
              <tbody>
                {allTransactions.map((txn, idx) => {
                  const badge = getTypeBadge(txn.type);
                  return (
                    <tr key={idx} style={{ borderBottom: idx < allTransactions.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                      <td style={{ padding: '10px 12px', color: '#374151', whiteSpace: 'nowrap' }}>
                        {formatDate(txn.date)}
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: '9999px',
                          fontSize: '11px',
                          fontWeight: 600,
                          backgroundColor: badge.bg,
                          color: badge.color,
                        }}>
                          {txn.type}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#111827' }}>
                        {formatINR(txn.amount)}
                      </td>
                      <td style={{ padding: '10px 12px', color: '#6b7280', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {txn.narration}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: '9999px',
                          fontSize: '11px',
                          fontWeight: 600,
                          color: '#ffffff',
                          backgroundColor: txn.severity.color,
                        }}>
                          {txn.severity.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ─── 3. Repayment Heatmap ─── */}
      <div>
        <h3 style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '12px' }}>
          Repayment Heatmap
        </h3>
        {(!report.repaymentHeatmap || report.repaymentHeatmap.length === 0) ? (
          <div style={{ padding: '24px', textAlign: 'center', color: '#9ca3af', fontSize: '13px', backgroundColor: '#f9fafb', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
            No repayment data available
          </div>
        ) : (
          <div style={{ padding: '16px', borderRadius: '12px', backgroundColor: '#ffffff', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
              {report.repaymentHeatmap.map((entry) => {
                const monthLabel = (() => {
                  try {
                    const d = new Date(entry.month + '-01');
                    return d.toLocaleString('en-US', { month: 'short' });
                  } catch {
                    return entry.month;
                  }
                })();
                return (
                  <div key={entry.month} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                    <div
                      style={{
                        width: '48px',
                        height: '40px',
                        borderRadius: '8px',
                        backgroundColor: HEATMAP_COLORS[entry.status] ?? HEATMAP_COLORS['no-data'],
                      }}
                      title={`${monthLabel}: ${entry.status} (EMIs: ${entry.emiCount}, Bounces: ${entry.bounceCount})`}
                    />
                    <span style={{ fontSize: '10px', color: '#6b7280', fontWeight: 500 }}>
                      {monthLabel}
                    </span>
                  </div>
                );
              })}
            </div>
            {/* Legend */}
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', marginTop: '12px' }}>
              {[
                { label: 'On-time', color: HEATMAP_COLORS['on-time'] },
                { label: 'Late', color: HEATMAP_COLORS['late'] },
                { label: 'Bounced', color: HEATMAP_COLORS['bounced'] },
                { label: 'No data', color: HEATMAP_COLORS['no-data'] },
              ].map((item) => (
                <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '2px', backgroundColor: item.color }} />
                  <span style={{ fontSize: '10px', color: '#6b7280' }}>{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
